import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Printer,
  Layers,
  Copy,
  ScanLine,
  ArrowRight,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { gbp } from "@/lib/api";

const services = [
  {
    icon: Printer,
    title: "Printing",
    price: 1.0,
    desc: "Crisp, high-resolution prints on premium paper stock — from single sheets to bulk runs.",
    span: "lg:col-span-2",
  },
  {
    icon: Layers,
    title: "Laminating",
    price: 2.0,
    desc: "Durable, professional laminate finishes that protect and elevate your documents.",
    span: "",
  },
  {
    icon: Copy,
    title: "Copying",
    price: 3.0,
    desc: "Fast, faithful reproductions with true-to-original clarity and colour.",
    span: "",
  },
  {
    icon: ScanLine,
    title: "Scanning",
    price: 4.0,
    desc: "High-fidelity digital scans of your physical documents, images and archives.",
    span: "lg:col-span-2",
  },
];

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App" data-testid="home-page">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black/10">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-12 lg:px-10 lg:py-28">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 border border-black/10 bg-white px-3 py-1.5 text-xs font-medium uppercase tracking-[0.15em] text-[var(--text-secondary)]"
            >
              <MapPin size={13} className="text-[var(--brand-primary)]" /> Victoria Road
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tight text-[var(--text-primary)] sm:text-6xl lg:text-7xl"
            >
              Your local print &<br />
              <span className="text-[var(--brand-primary)]">document</span> experts.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-[var(--text-secondary)]"
            >
              NLCS — Nathans Laminating and Copying Service — delivers high-quality
              printing, laminating, copying and scanning. Upload your files, choose your
              services and pay securely — all in one place.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/order"
                data-testid="hero-order-cta"
                className="group inline-flex items-center gap-2 bg-[var(--brand-primary)] px-7 py-4 text-base font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[var(--brand-primary-hover)]"
              >
                Place an Order
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <span className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                <ShieldCheck size={16} className="text-[var(--brand-primary)]" />
                Secure Square checkout
              </span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative lg:col-span-5"
          >
            <div className="relative aspect-[4/5] overflow-hidden border border-black/10">
              <img
                src="https://images.unsplash.com/photo-1758183961426-88d64eb5f787?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxwcmludGluZyUyMHByZXNzJTIwbW9kZXJufGVufDB8fHx8MTc4MzcyMDE5NHww&ixlib=rb-4.1.0&q=85"
                alt="Modern printing press"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden bg-[var(--brand-primary)] px-6 py-4 text-white sm:block">
              <div className="font-display text-3xl font-extrabold">4</div>
              <div className="text-xs uppercase tracking-widest text-white/80">Core services</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT / SERVICES */}
      <section id="about" className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-primary)]">
            About Us
          </p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[var(--text-primary)] sm:text-4xl">
            Everything you need, done right.
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-[var(--text-secondary)]">
            Based on Victoria Road, NLCS is a trusted local service offering four core
            capabilities. Whatever the job, we combine quality craftsmanship with quick
            turnaround and transparent pricing.
          </p>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`group border border-black/10 bg-white p-8 transition-all hover:border-[var(--brand-primary)] hover:shadow-md ${s.span}`}
            >
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] transition-colors group-hover:bg-[var(--brand-primary)] group-hover:text-white">
                  <s.icon size={22} />
                </span>
                <span className="font-mono text-sm text-[var(--text-secondary)]">
                  from {gbp(s.price)}
                </span>
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold text-[var(--text-primary)]">
                {s.title}
              </h3>
              <p className="mt-2 text-base leading-relaxed text-[var(--text-secondary)]">
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-[var(--brand-primary)]">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 py-20 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <div className="max-w-2xl">
            <h2 className="font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">
              Ready to get started?
            </h2>
            <p className="mt-4 text-lg text-white/80">
              Upload your files, pick your services and see your total update live. Pay
              securely and we'll take it from there.
            </p>
          </div>
          <Link
            to="/order"
            data-testid="cta-order-button"
            className="group inline-flex shrink-0 items-center gap-3 bg-white px-8 py-5 text-lg font-bold text-[var(--brand-primary)] transition-all hover:-translate-y-0.5"
          >
            Place an Order
            <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
